import React from 'react';
import { useTranslation } from 'react-i18next';
import GenericTablePage from '../components/GenericTablePage';
import { GridColDef } from '@mui/x-data-grid';
import { Chip } from '@mui/material';
import { formatDate } from '../lib/dateUtils';
import { enumChip } from '../lib/enumLabels';

const FundSystemPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar' : 'en';

  const columns: GridColDef[] = [
    { field: 'subscription_number', headerName: t('fundSystem.subscriptionNumber'), width: 150 },
    { field: 'id_card', headerName: t('fundSystem.idCard'), width: 150 },
    { field: 'fund_amount', headerName: t('fundSystem.fundAmount'), width: 150 },
    {
      field: 'payment_status',
      headerName: t('fundSystem.paymentStatus'),
      width: 150,
      renderCell: (params) => {
        const chip = enumChip('payment_status', params.value, locale);
        return chip ? <Chip label={chip.label} color={chip.color} size="small" /> : '-';
      },
    },
    {
      field: 'fees_paid_bool',
      headerName: t('fundSystem.feesPaid'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common.yes') : t('common.no')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'payment_date',
      headerName: t('fundSystem.paymentDate'),
      width: 150,
      valueFormatter: (value) => formatDate(value, locale, 'medium'),
    },
  ];

  const fields = [
    { 
      name: 'partner_id', 
      label: t('fundSystem.partner'), 
      type: 'select' as const,
      foreignKey: {
        table: 'partners',
        valueField: 'id',
        labelField: 'name'
      }
    },
    { 
      name: 'company_id', 
      label: t('fundSystem.company'), 
      type: 'select' as const,
      foreignKey: {
        table: 'companies',
        valueField: 'id',
        labelField: 'name'
      }
    },
    { 
      name: 'subscription_number', 
      label: t('fundSystem.subscriptionNumber'), 
      type: 'text' as const 
    },
    { 
      name: 'meeting_sub_number', 
      label: t('fundSystem.meetingSubNumber'), 
      type: 'text' as const 
    },
    { 
      name: 'sub_year', 
      label: t('fundSystem.subYear'), 
      type: 'number' as const 
    },
    { 
      name: 'fund_amount', 
      label: t('fundSystem.fundAmount'), 
      type: 'number' as const 
    },
    { 
      name: 'subscription_fees', 
      label: t('fundSystem.subscriptionFees'), 
      type: 'number' as const 
    },
    { 
      name: 'payment_date', 
      label: t('fundSystem.paymentDate'), 
      type: 'date' as const 
    },
    { 
      name: 'id_card', 
      label: t('fundSystem.idCard'), 
      type: 'text' as const 
    },
    { 
      name: 'birth_date', 
      label: t('fundSystem.birthDate'), 
      type: 'date' as const 
    },
    { 
      name: 'fees_paid_bool', 
      label: t('fundSystem.feesPaid'), 
      type: 'boolean' as const 
    },
    { 
      name: 'payment_status', 
      label: t('fundSystem.paymentStatus'), 
      type: 'select' as const,
      options: [
        { value: 'pending', label: t('fundSystem.pending') },
        { value: 'paid', label: t('fundSystem.paid') },
        { value: 'overdue', label: t('fundSystem.overdue') },
        { value: 'cancelled', label: t('fundSystem.cancelled') }
      ]
    },
  ];

  return (
    <GenericTablePage
      tableName="fund_system"
      title={t('fundSystem.title')}
      columns={columns}
      fields={fields}
      selectQuery="*, partner_id(name), company_id(name)"
      detailPath={(id) => `/fund-system/${id}`}
      editPath={(id) => (id ? `/fund-system/${id}/edit` : '/fund-system/new')}
    />
  );
};

export default FundSystemPage;
